import React, { useEffect, useState, useRef } from 'react';
import { Stage, Layer, Image as KonvaImage } from 'react-konva';
import useImage from 'use-image';
import EditableRect from './EditableRect';
import { normalizedToPixel, pixelToNormalized } from '../../utils/coordinateUtils';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { toast } from 'react-toastify';

const TARGET_STAGE_SIZE = 416; // Define the target size

const ImageCanvas = ({
  imageUrl,
  annotations,
  setAnnotations,
  selectedId,
  setSelectedId,
  imageSize, // Still needed for normalization logic
  setImageSize, // Still needed to store original size
  isLoadingImage,
  imageLoadError,
  showTiltAngles
}) => {
  const [image, imageStatus] = useImage(imageUrl, 'anonymous');
  // Set the stage size to the fixed target size
  const [stageSize, setStageSize] = useState({ width: TARGET_STAGE_SIZE, height: TARGET_STAGE_SIZE });
  const [copiedAnnotation, setCopiedAnnotation] = useState(null);
  const stageRef = useRef(null);
  const containerRef = useRef(null); // Ref for the container div that handles focus

  // Store original image dimensions when loaded
  useEffect(() => {
    if (image && imageStatus === 'loaded') {
      // Store the actual image size
      setImageSize([image.width, image.height]);
      // Stage size remains fixed at TARGET_STAGE_SIZE x TARGET_STAGE_SIZE
    }
    if (imageStatus === 'failed') {
      console.error("Failed to load image:", imageUrl);
    }
  }, [image, imageStatus, setImageSize, imageUrl]);

  // Focus the container on mount/image change
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.focus();
    }
    // Dependency array intentionally left empty to only run on mount,
    // or add imageUrl if you want re-focus on image change
  }, []);

  // Calculate scaling factors only when imageSize is available
  const [scaleFactors, setScaleFactors] = useState({ x: 1, y: 1 });
  useEffect(() => {
    if (imageSize && imageSize[0] > 0 && imageSize[1] > 0) {
      setScaleFactors({
        x: stageSize.width / imageSize[0],
        y: stageSize.height / imageSize[1]
      });
    } else {
      setScaleFactors({ x: 1, y: 1 }); // Default if image size is invalid
    }
  }, [imageSize, stageSize]);


  const handleAnnotationChange = (id, newImagePixelAttrs) => {
    // newImagePixelAttrs are already converted back to image-relative pixels by the onChange handler below
    if (!imageSize || imageSize[0] === 0 || imageSize[1] === 0) {
      console.error("Cannot update annotation, original image size not available.");
      return;
    }

    // Ensure other properties like tilt_angle are preserved
    const existingAnn = annotations.find(ann => ann.id === id);
    if (!existingAnn) {
      console.error(`Annotation with ID ${id} not found for update.`);
      return;
    }

    // Convert the image-relative pixel box back to normalized format
    const newNormalizedBox = pixelToNormalized(newImagePixelAttrs, imageSize);

    setAnnotations(prevAnnotations =>
      prevAnnotations.map(ann =>
        ann.id === id
          ? {
            ...existingAnn, // Keep all original data (like tilt, label)
            box_2d: newNormalizedBox, // Update only the box
          }
          : ann
      )
    );
  };

  const handleContainerClick = (e) => {
    if (containerRef.current && (e.target === containerRef.current || e.target === stageRef.current?.getStage())) {
      containerRef.current.focus();
    }
    if (e.target === stageRef.current?.getStage()) {
      setSelectedId(null);
    }
  };

  const handleStageMouseDown = (e) => {
    if (e.target === e.target.getStage()) {
      setSelectedId(null);
      containerRef.current?.focus();
    }
  };


  const handleKeyDown = (e) => {
    if (e.ctrlKey) {
      if (e.key === 'c') {
        e.preventDefault();
        if (selectedId) {
          const selectedAnnotation = annotations.find(ann => ann.id === selectedId);
          if (selectedAnnotation) {
            setCopiedAnnotation({ ...selectedAnnotation }); // Copy the whole annotation object
            console.log('Annotation copied:', selectedAnnotation);
            toast.info('Annotation copied!');
          } else {
            console.log('Copy failed: Selected ID not found.');
          }
        } else {
          console.log('Copy skipped: No annotation selected.');
        }
      } else if (e.key === 'v') {
        e.preventDefault();
        if (copiedAnnotation && stageRef.current) {
          console.log('Pasting annotation:', copiedAnnotation);
          const stage = stageRef.current;
          let pointerPosition = stage.getPointerPosition();

          if (!pointerPosition) {
            console.warn("Pointer position not found, pasting in center.");
            pointerPosition = { x: stage.width() / 2, y: stage.height() / 2 };
          }

          // --- Paste Logic ---
          // 1. Convert stage pointer position to image-relative pixel position
          if (!imageSize || imageSize[0] <= 0 || imageSize[1] <= 0) {
            console.error("Cannot paste: Original image size unknown.");
            return;
          }
          // Use the calculated scaleFactors' inverses
          const imagePointerX = pointerPosition.x / scaleFactors.x;
          const imagePointerY = pointerPosition.y / scaleFactors.y;

          // 2. Convert image-relative pixel position to normalized coordinates
          const normalizedPointerPos = pixelToNormalized(
            { x: imagePointerX, y: imagePointerY, width: 0, height: 0 },
            imageSize
          );

          // 3. Calculate the size (height/width) of the copied box in normalized coords
          const copiedHeightNorm = copiedAnnotation.box_2d[2] - copiedAnnotation.box_2d[0];
          const copiedWidthNorm = copiedAnnotation.box_2d[3] - copiedAnnotation.box_2d[1];

          // 4. Calculate new normalized box, centered around pointer + offset
          const pasteOffsetXNorm = 20; // ~2% normalized offset
          const pasteOffsetYNorm = 20;

          const newYminNorm = normalizedPointerPos[0] + pasteOffsetYNorm - copiedHeightNorm / 2;
          const newXminNorm = normalizedPointerPos[1] + pasteOffsetXNorm - copiedWidthNorm / 2;
          const newYmaxNorm = newYminNorm + copiedHeightNorm;
          const newXmaxNorm = newXminNorm + copiedWidthNorm;

          // 5. Clamp and round the new normalized box
          const newBoxNorm = [
            newYminNorm, newXminNorm, newYmaxNorm, newXmaxNorm
          ].map(coord => Math.round(Math.max(0, Math.min(1000, coord))));

          console.log('Calculated new box (normalized):', newBoxNorm);

          // 6. Create the new annotation object
          const newAnnotation = {
            // Create a new unique ID
            id: `${copiedAnnotation.annotationType}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
            // Copy relevant properties
            annotationType: copiedAnnotation.annotationType,
            label: copiedAnnotation.label,
            box_2d: newBoxNorm, // Use the newly calculated normalized box
            tilt_angle: copiedAnnotation.tilt_angle // Copy tilt angle
          };

          console.log('Adding new annotation from paste:', newAnnotation);
          setAnnotations(prev => [...prev, newAnnotation]);
          setSelectedId(newAnnotation.id);
          toast.info('Annotation pasted!');
        } else {
          console.log(`Paste skipped: ${copiedAnnotation ? 'Stage ref missing?' : 'No annotation copied'}.`);
        }
      }
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      if (selectedId) {
        console.log('Deleting annotation:', selectedId);
        setAnnotations(prevAnnotations => prevAnnotations.filter((a) => a.id !== selectedId));
        setSelectedId(null);
        toast.info('Annotation deleted!');
      } else {
        console.log('Delete skipped: No annotation selected.');
      }
    }
  };

  // --- Rendering Logic ---

  if (isLoadingImage || (!image && imageStatus === 'loading')) {
    // Maintain fixed size for loading box as well
    return (
      <Box sx={{ width: TARGET_STAGE_SIZE, height: TARGET_STAGE_SIZE, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ccc', bgcolor: 'grey.100', margin: 'auto' /* Center the box */ }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Image...</Typography>
      </Box>
    );
  }

  if (imageLoadError || imageStatus === 'failed') {
    return (
      <Box sx={{ width: TARGET_STAGE_SIZE, height: TARGET_STAGE_SIZE, border: '1px solid #ccc', bgcolor: 'grey.100', margin: 'auto' }}>
        <Alert severity="error" sx={{ m: 2 }}>
          Error loading image: {imageLoadError || 'Unknown error'}
        </Alert>
      </Box>
    );
  }

  if (!image) {
    return (
      <Box sx={{ width: TARGET_STAGE_SIZE, height: TARGET_STAGE_SIZE, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ccc', bgcolor: 'grey.100', margin: 'auto' }}>
        <Typography>Image not available.</Typography>
      </Box>
    );
  }

  // Render Stage inside a div that can receive focus
  return (
    <div
      ref={containerRef}
      tabIndex={0} // Make this div focusable
      onKeyDown={handleKeyDown} // Attach keydown listener here
      onClick={handleContainerClick} // Handle clicks for focus management
      style={{
        outline: 'none', // Hide the default focus outline
        border: '1px solid #ccc', // Keep the border visual
        // Set fixed size for the container div
        width: `${TARGET_STAGE_SIZE}px`,
        height: `${TARGET_STAGE_SIZE}px`,
        overflow: 'hidden', // Hide parts of image/stage if they exceed this
        position: 'relative', // For potential absolute positioning inside
        margin: 'auto' // Center the container div if parent allows
      }}
    >
      <Stage
        width={stageSize.width} // Should be TARGET_STAGE_SIZE
        height={stageSize.height} // Should be TARGET_STAGE_SIZE
        onMouseDown={handleStageMouseDown}
        ref={stageRef}
      >
        <Layer>
          {/* Draw the image scaled to fit the stage */}
          <KonvaImage
            image={image}
            width={stageSize.width}
            height={stageSize.height}
          />
          {/* Render annotations only if original image size is known */}
          {imageSize && imageSize[0] > 0 && imageSize[1] > 0 && annotations.map((ann) => {
            const annotationWithTilt = {
              ...ann,
              tilt_angle: ann.annotationType === 'roof' && ann.tilt_angle === undefined ? 0 : ann.tilt_angle
            };

            // 1. Convert normalized box to pixel box relative to ORIGINAL image size
            const imagePixelBox = normalizedToPixel(annotationWithTilt.box_2d, imageSize);

            // 2. Scale these image-relative pixels to the fixed STAGE size
            const stagePixelBox = {
              id: annotationWithTilt.id, // Keep the ID
              x: imagePixelBox.x * scaleFactors.x,
              y: imagePixelBox.y * scaleFactors.y,
              width: imagePixelBox.width * scaleFactors.x,
              height: imagePixelBox.height * scaleFactors.y,
              tilt_angle: annotationWithTilt.tilt_angle // Pass tilt angle along
            };

            // Add a check for minimum size on stage to prevent rendering tiny boxes
            if (stagePixelBox.width < 2 || stagePixelBox.height < 2) {
              // console.warn(`Skipping rendering annotation ID ${annotationWithTilt.id} due to very small stage dimensions:`, stagePixelBox);
              return null;
            }
            const color = annotationWithTilt.annotationType === 'roof' ? 'red' : 'green';

            return (
              <EditableRect
                key={annotationWithTilt.id}
                shapeProps={stagePixelBox} // Use stage-scaled pixel box for rendering
                color={color}
                isSelected={selectedId === annotationWithTilt.id}
                onSelect={() => {
                  setSelectedId(annotationWithTilt.id);
                  containerRef.current?.focus();
                }}
                onChange={(newStagePixelAttrs) => {
                  // Convert stage pixel attributes back to IMAGE pixel attributes
                  // Use the inverse of the scaling factors
                  const imagePixelAttrs = {
                    ...newStagePixelAttrs, // Keep id, tilt_angle etc.
                    x: newStagePixelAttrs.x / scaleFactors.x,
                    y: newStagePixelAttrs.y / scaleFactors.y,
                    width: newStagePixelAttrs.width / scaleFactors.x,
                    height: newStagePixelAttrs.height / scaleFactors.y,
                  };
                  // console.log("onChange - Converted back to ImagePixelAttrs:", imagePixelAttrs);
                  handleAnnotationChange(annotationWithTilt.id, imagePixelAttrs);
                }}
                showTiltAngles={showTiltAngles}
              />
            );
          })}
        </Layer>
      </Stage>
    </div>
  );
};

export default ImageCanvas;